import { createApi } from '@reduxjs/toolkit/query/react';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';
import {
  AdminClientFiltersResponse,
  AdminFilterCreatePayload,
  AdminFilterIdResponse,
  AdminFilterOrderPayload,
  AdminFilterUpdatePayload,
} from 'store/clientFilters/types';

type ClientArg = { clientId: number | string };

export const clientFiltersApi = createApi({
  reducerPath: 'clientFiltersApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ClientFilters'],
  endpoints: builder => ({
    getClientFilters: builder.query<AdminClientFiltersResponse, ClientArg>({
      query: ({ clientId }) => API_ROUTES.CLIENT_FILTERS(clientId),
      providesTags: (result, error, { clientId }) => [
        { type: 'ClientFilters' as const, id: clientId },
      ],
    }),

    // The response only carries the new id, and the new filter's rating is set by the API,
    // so the list is refetched rather than patched.
    createClientFilter: builder.mutation<
      AdminFilterIdResponse,
      ClientArg & { body: AdminFilterCreatePayload }
    >({
      query: ({ clientId, body }) => ({
        url: API_ROUTES.CLIENT_FILTERS(clientId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { clientId }) =>
        result ? [{ type: 'ClientFilters' as const, id: clientId }] : [],
    }),

    updateClientFilter: builder.mutation<
      AdminFilterIdResponse,
      ClientArg & { filterId: string; body: AdminFilterUpdatePayload }
    >({
      query: ({ clientId, filterId, body }) => ({
        url: API_ROUTES.CLIENT_FILTER(clientId, filterId),
        method: 'PATCH',
        body,
      }),
      // Patched in place instead of refetched, which would hide the table behind the loader
      // after each status switch. The response only carries the id, so the sent fields are merged.
      onQueryStarted: async ({ clientId, filterId, body }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch {
          // The caller unwraps the mutation and handles the failure.
          return;
        }
        dispatch(
          clientFiltersApi.util.updateQueryData('getClientFilters', { clientId }, draft => {
            const filter = draft.filters.find(({ filter_id }) => filter_id === filterId);
            if (filter) Object.assign(filter, body);
          })
        );
      },
    }),

    reorderClientFilters: builder.mutation<
      AdminClientFiltersResponse,
      ClientArg & { body: AdminFilterOrderPayload }
    >({
      query: ({ clientId, body }) => ({
        url: API_ROUTES.CLIENT_FILTERS(clientId),
        method: 'PUT',
        body,
      }),
      // The response is the client's full list, so it replaces the cache instead of a refetch.
      // Not upsertQueryData: it marks the entry pending, which hides the list behind the loader.
      onQueryStarted: async ({ clientId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            clientFiltersApi.util.updateQueryData('getClientFilters', { clientId }, () => data)
          );
        } catch {
          // The caller unwraps the mutation and handles the failure.
        }
      },
    }),
  }),
});

export const {
  useGetClientFiltersQuery,
  useCreateClientFilterMutation,
  useUpdateClientFilterMutation,
  useReorderClientFiltersMutation,
} = clientFiltersApi;
